import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip,
  Skeleton,
  Alert,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  Divider,
} from "@mui/material";
import {
  Groups as GroupsIcon,
  People as PeopleIcon,
  Refresh as RefreshIcon,
  Event as EventIcon,
  Star as StarIcon,
  SupportAgent as AssistantIcon,
  ChevronRight as ViewIcon,
  Assessment as AssessmentIcon,
} from "@mui/icons-material";
import { Link as RouterLink } from "react-router-dom";
import api from "../../api";
import { groupRoleConfig } from "./tutorStatusChips";
import GroupProgressReportDialog from "./GroupProgressReportDialog";

const formatDate = (iso) =>
  iso ? new Date(iso).toLocaleDateString(undefined, { dateStyle: "medium" }) : "—";

/** Label and colour are shared with the dashboard; only the chip icon is local to this page. */
const roleConfig = {
  tutor: { ...groupRoleConfig.tutor, icon: <StarIcon fontSize="small" /> },
  helper: { ...groupRoleConfig.helper, icon: <AssistantIcon fontSize="small" /> },
};

export default function TutorGroups() {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [selected, setSelected] = useState(null);
  const [students, setStudents] = useState([]);
  const [studentsLoading, setStudentsLoading] = useState(false);
  const [studentsError, setStudentsError] = useState("");
  const [groupReportOpen, setGroupReportOpen] = useState(false);

  const loadGroups = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await api.get("tutor/groups");
      setGroups(Array.isArray(res.data?.data) ? res.data.data : []);
    } catch (e) {
      setError(e?.response?.data?.message || "Could not load your groups.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadGroups();
  }, [loadGroups]);

  const openStudents = async (group) => {
    setSelected(group);
    setStudents([]);
    setStudentsError("");
    setStudentsLoading(true);
    try {
      const res = await api.get(`tutor/groups/${group.id}/students`);
      setStudents(res.data?.data?.students ?? []);
    } catch (e) {
      setStudentsError(e?.response?.data?.message || "Could not load students for this group.");
    } finally {
      setStudentsLoading(false);
    }
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <GroupsIcon color="primary" />
          <Typography variant="h5" fontWeight={700}>
            My Groups
          </Typography>
        </Box>
        <Button startIcon={<RefreshIcon />} onClick={loadGroups} disabled={loading}>
          Refresh
        </Button>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Course plans you run as lead tutor, or support as a tutor assistant.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Grid container spacing={2}>
          {[1, 2, 3].map((n) => (
            <Grid item xs={12} md={6} lg={4} key={n}>
              <Skeleton variant="rounded" height={190} />
            </Grid>
          ))}
        </Grid>
      ) : groups.length === 0 && !error ? (
        <Alert severity="info">
          You are not assigned to any group yet. An admin assigns tutors and tutor assistants to a course plan.
        </Alert>
      ) : (
        <Grid container spacing={2}>
          {groups.map((g) => {
            const role = roleConfig[g.my_role] || { label: g.my_role, color: "#616161" };
            return (
              <Grid item xs={12} md={6} lg={4} key={g.id}>
                <Card
                  variant="outlined"
                  sx={{
                    height: "100%",
                    borderLeft: `4px solid ${role.color}`,
                    opacity: g.is_active ? 1 : 0.65,
                  }}
                >
                  <CardContent>
                    <Stack direction="row" spacing={1} sx={{ mb: 1, flexWrap: "wrap", gap: 0.5 }}>
                      <Chip
                        size="small"
                        icon={role.icon}
                        label={role.label}
                        sx={{ bgcolor: `${role.color}18`, color: role.color, fontWeight: 600 }}
                      />
                      {!g.is_active && <Chip size="small" label="Ended" color="default" />}
                      {g.mode && <Chip size="small" variant="outlined" label={g.mode.replace("_", " ")} />}
                    </Stack>

                    <Typography variant="h6" fontWeight={700} gutterBottom>
                      {g.name}
                    </Typography>
                    {g.course?.name && (
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        {g.course.name}
                      </Typography>
                    )}

                    <Divider sx={{ my: 1.5 }} />

                    <Stack spacing={0.75}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <EventIcon fontSize="small" color="action" />
                        <Typography variant="body2">
                          {formatDate(g.start_date)} – {formatDate(g.end_date)}
                        </Typography>
                      </Box>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <PeopleIcon fontSize="small" color="action" />
                        <Typography variant="body2">
                          {g.student_count} student{g.student_count === 1 ? "" : "s"}
                        </Typography>
                      </Box>
                      {g.my_role !== "tutor" && g.lead_tutor && (
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <StarIcon fontSize="small" color="action" />
                          <Typography variant="body2" color="text.secondary">
                            Lead: {g.lead_tutor}
                          </Typography>
                        </Box>
                      )}
                    </Stack>

                    <Button
                      fullWidth
                      variant="outlined"
                      sx={{ mt: 2 }}
                      startIcon={<PeopleIcon />}
                      onClick={() => openStudents(g)}
                    >
                      View students
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}

      <Dialog open={Boolean(selected)} onClose={() => setSelected(null)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {selected?.name} — students
          {!studentsLoading && students.length > 0 && (
            <Typography variant="body2" color="text.secondary">
              {students.length} enrolled
            </Typography>
          )}
        </DialogTitle>
        <DialogContent dividers>
          {studentsError && <Alert severity="error">{studentsError}</Alert>}
          {studentsLoading ? (
            <Skeleton variant="rounded" height={180} />
          ) : (
            !studentsError && (
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Name</TableCell>
                      <TableCell>Email</TableCell>
                      <TableCell>School year</TableCell>
                      <TableCell>Joined</TableCell>
                      <TableCell align="right">Progress</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {students.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} align="center" sx={{ py: 3 }}>
                          <Typography variant="body2" color="text.secondary">
                            No students are enrolled in this group yet.
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Students appear here once an admin enrols them on this group's plan.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      students.map((s) => (
                        <TableRow key={s.id} hover>
                          <TableCell>{s.name}</TableCell>
                          <TableCell>{s.email}</TableCell>
                          <TableCell>{s.school_year || "—"}</TableCell>
                          <TableCell>
                            {s.joined_at ? new Date(s.joined_at).toLocaleDateString() : "—"}
                          </TableCell>
                          <TableCell align="right">
                            {/* The roster id is the buyer id this page is keyed on, so it links
                                straight into the per-student progress view that already exists
                                rather than duplicating it inside this dialog. */}
                            <Button
                              size="small"
                              endIcon={<ViewIcon />}
                              component={RouterLink}
                              to={`/tutor/students/${s.id}`}
                            >
                              View
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )
          )}
        </DialogContent>
        <DialogActions>
          <Button
            startIcon={<AssessmentIcon />}
            onClick={() => setGroupReportOpen(true)}
            disabled={studentsLoading || students.length === 0}
          >
            Send progress reports
          </Button>
          <Button onClick={() => setSelected(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      <GroupProgressReportDialog
        open={groupReportOpen}
        group={selected}
        recipientCount={students.length}
        onClose={() => setGroupReportOpen(false)}
      />
    </Box>
  );
}
